import { computed, ref, shallowRef } from "vue"
import { defineStore } from "pinia"
import type { RecordType as CoreRecordType, RecordTypeSchema } from "@auger/core"
import {
  getSearchableSourceKeysForRecordType,
  getSourceTypesForRecordType,
} from "@/common/sourceRegistry"
import { getAuger } from "@/init/auger"

export { resolveSourceType } from "@/common/sourceRegistry"

export const FALLBACK_ICON = "circle-question"
export const FALLBACK_COLOR = "#3273dc"

/** Core's record type plus what `enrichType` stamps on for the app. */
export interface RecordType extends CoreRecordType {
  sourceTypes?: ReturnType<typeof getSourceTypesForRecordType>
  searchableSourceTypes?: ReturnType<typeof getSearchableSourceKeysForRecordType>
}

// Fallbacks for a name the catalog does not hold; `enrichType` adds `id`.
export const staticTypes: Record<string, Omit<CoreRecordType, "id">> = {
  user: {
    name: "user",
    label: "User",
    plural_label: "Users",
    position: -1,
    description: null,
  },
  organization: {
    name: "organization",
    label: "Organization",
    plural_label: "Organizations",
    position: 0,
    description: null,
  },
  person: {
    name: "person",
    label: "Person",
    plural_label: "People",
    position: 1,
    description: null,
  },
  event: {
    name: "event",
    label: "Event",
    plural_label: "Events",
    position: 2,
    description: null,
  },
  list: {
    name: "list",
    label: "List",
    plural_label: "Lists",
    position: 3,
    description: null,
  },
}

// `id` mirrors the primary key (`types.name` — the table has no id column), so a
// type satisfies the entity contract the reference layer holds every target to:
// `referenceEntities` keeps only entities with an `id` and aligns them to the
// stored value by it, and the draft→id write paths read `id` back out. Without it
// a resolved type reads as an orphan and renders "Unknown ID: <name>". The
// catalog's types already carry it; the static fallbacks get it here.
function enrichType(type: Omit<CoreRecordType, "id">): RecordType {
  return {
    ...type,
    id: type.name,
    sourceTypes: getSourceTypesForRecordType(type.name),
    searchableSourceTypes: getSearchableSourceKeysForRecordType(type.name),
  }
}

// The lookups take a nullish name because callers routinely have one — a record
// that hasn't loaded, an optional link. They already answer "not found" for a
// miss, so demanding a definite string only bought casts at the call sites.
export const getType = (name?: string | null): RecordType | undefined =>
  name ? useTypesStore().get(name) : undefined
export const getTypeIcon = (name?: string | null): string =>
  getType(name)?.schema?.icon || FALLBACK_ICON
export const getTypeColor = (name?: string | null): string =>
  getType(name)?.schema?.color || FALLBACK_COLOR
export const getTypeLabel = (name: string, count = 1): string => {
  const type = getType(name)
  if (!name || !type) return count !== 1 ? "records" : "record"
  return (count !== 1 ? type.plural_label : type.label) ?? name
}
export const getTypeSchema = (name?: string | null): RecordTypeSchema | null =>
  getType(name)?.schema || null
export const isValidTypeName = (name: string): boolean => useTypesStore().typeNames.includes(name)
export const getAllTypes = (): RecordType[] => useTypesStore().types
export const getEntityTypes = (): RecordType[] => useTypesStore().entityTypes

// A view over @auger/core's catalog, which loads, holds and writes the types:
// a type write through core shows up here with no store patching.
export const useTypesStore = defineStore("types", () => {
  const catalog = getAuger().catalog
  const read = () => catalog.types().map(enrichType)
  const types = shallowRef<RecordType[]>(read())
  const loading = ref(false)
  const initialized = ref(catalog.snapshot() != null)
  catalog.onChange(() => {
    types.value = read()
    initialized.value = true
  })

  const byName = computed<Record<string, RecordType>>(() =>
    Object.fromEntries(types.value.map((t) => [t.name, t]))
  )
  const typeNames = computed(() => types.value.map((t) => t.name))
  const entityTypes = computed(() => types.value.filter((t) => t.name !== "list"))

  const get = (name: string): RecordType =>
    byName.value[name] || enrichType(staticTypes[name] || { name, label: name, plural_label: name })

  /** Make the catalog current (see core's `catalog.refresh`). */
  const fetch = () => {
    loading.value = true
    return catalog
      .refresh()
      .then(() => types.value)
      .finally(() => {
        loading.value = false
      })
  }

  return {
    types,
    loading,
    initialized,
    byName,
    typeNames,
    entityTypes,
    get,
    fetch,
  }
})
